import React from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qrcode-logo'

import { useWindowResize } from '@utils/domUtils'

import { PlatformInfoDto, PlatformInfoPlatformLinkDto } from '@model/PlatformInfoDto'
import {
  OperatingSystemEnum,
  detectAndroidOperatingSystem,
  detectIOSOperatingSystem,
  isTMTPlatformType,
  isWebTraderOperatingSystem,
} from '@model/PlatformTypeEnum'

import { APKBanner } from '@assets/banners/APK.banner'
import { AppStoreBanner } from '@assets/banners/AppStore.banner'
import { GooglePlayBanner } from '@assets/banners/GooglePlay.banner'

import aboutDesktop from '../images/TickmillTrader/Images/aboutDesktop.webp'
import aboutMobile from '../images/TickmillTrader/Images/aboutMobile.webp'
import buysellDesktop from '../images/TickmillTrader/Images/buysellDesktop.webp'
import buysellMobile from '../images/TickmillTrader/Images/buysellMobile.webp'
import chartDesktop from '../images/TickmillTrader/Images/chartDesktop.webp'
import chartMobile from '../images/TickmillTrader/Images/chartMobile.webp'
import managementDesktop from '../images/TickmillTrader/Images/managementDesktop.webp'
import managementMobile from '../images/TickmillTrader/Images/managementMobile.webp'
import watchlistDesktop from '../images/TickmillTrader/Images/watchlistDesktop.webp'
import watchlistMobile from '../images/TickmillTrader/Images/watchlistMobile.webp'
import webtraderBuySellDesktop from '../images/TickmillTrader/Images/webtraderBuySellDesktop.webp'
import webtraderBuySellMobile from '../images/TickmillTrader/Images/webtraderBuySellMobile.webp'
import webtraderChartsDesktop from '../images/TickmillTrader/Images/webtraderChartsDesktop.webp'
import webtraderChartsMobile from '../images/TickmillTrader/Images/webtraderChartsMobile.webp'
import webtraderLayoutsWorkspacesDesktop from '../images/TickmillTrader/Images/webtraderLayoutsWorkspacesDesktop.webp'
import webtraderLayoutsWorkspacesMobile from '../images/TickmillTrader/Images/webtraderLayoutsWorkspacesMobile.webp'
import webtraderWatchlistsDesktop from '../images/TickmillTrader/Images/webtraderWatchlistsDesktop.webp'
import webtraderWatchlistsMobile from '../images/TickmillTrader/Images/webtraderWatchlistsMobile.webp'
import webtraderWebTraderOverviewDesktop from '../images/TickmillTrader/Images/webtraderWebTraderOverviewDesktop.webp'
import webtraderWebTraderOverviewMobile from '../images/TickmillTrader/Images/webtraderWebTraderOverviewMobile.webp'

export interface TickmillTraderVideoData {
  image: string
  title: string
  text: string
  link: string
}

export const useTickmillTraderWebtraderVideosData = (): TickmillTraderVideoData[] => {
  const isMobile = useWindowResize()

  return [
    {
      image: isMobile ? webtraderWebTraderOverviewMobile : webtraderWebTraderOverviewDesktop,
      title: 'ToolsPlatforms.WebTrader overview',
      text: 'ToolsPlatforms.Discover seamless trading manage layouts, watchlists, orders, and powerful chart tools',
      link: 'https://youtu.be/jDTJJB9Pp4w',
    },
    {
      image: isMobile ? webtraderLayoutsWorkspacesMobile : webtraderLayoutsWorkspacesDesktop,
      title: 'ToolsPlatforms.Layouts and Workspaces',
      text: 'ToolsPlatforms.Effortlessly manage layouts, switch accounts, and create custom dashboards',
      link: 'https://youtu.be/XqxDGbZDVhs',
    },
    {
      image: isMobile ? webtraderChartsMobile : webtraderChartsDesktop,
      title: 'ToolsPlatforms.Charts',
      text: 'ToolsPlatforms.Explore charts manage indicators, change types, and use 90+ studies',
      link: 'https://youtu.be/3hMxTEAJG48',
    },
    {
      image: isMobile ? webtraderBuySellMobile : webtraderBuySellDesktop,
      title: 'ToolsPlatforms.Buy and Sell',
      text: 'ToolsPlatforms.Trade smarter place orders, manage trades, and one-click execution',
      link: 'https://youtu.be/ezdL2rb5BaM',
    },
    {
      image: isMobile ? webtraderWatchlistsMobile : webtraderWatchlistsDesktop,
      title: 'ToolsPlatforms.Watchlists',
      text: 'ToolsPlatforms.Customize watchlists, columns, trade maps, and manage instruments easily',
      link: 'https://youtu.be/VhfOlqZHdCE',
    },
  ]
}

export const useTickmillTraderAndroidVideosData = (): TickmillTraderVideoData[] => {
  const isMobile = useWindowResize()

  return [
    {
      image: isMobile ? aboutMobile : aboutDesktop,
      title: 'ToolsPlatforms.About the app',
      text: 'ToolsPlatforms.Keep control of your trading with easy access all in one place',
      link: 'https://youtube.com/shorts/CsMdjindduI',
    },
    {
      image: isMobile ? managementMobile : managementDesktop,
      title: 'ToolsPlatforms.Account Management',
      text: 'ToolsPlatforms.Get easy and secure login with face recognition and fingerprint',
      link: 'https://youtube.com/shorts/kQ7L0s5B5WM',
    },
    {
      image: isMobile ? buysellMobile : buysellDesktop,
      title: 'ToolsPlatforms.Buy and Sell',
      text: 'ToolsPlatforms.Seamless trading with low spreads at your fingertips',
      link: 'https://youtube.com/shorts/j-oyLMrb67w',
    },
    {
      image: isMobile ? watchlistMobile : watchlistDesktop,
      title: 'ToolsPlatforms.Watchlist',
      text: 'ToolsPlatforms.Create a watchlist with your favourite assets',
      link: 'https://youtube.com/shorts/Qq3uH29Evdg',
    },
    {
      image: isMobile ? chartMobile : chartDesktop,
      title: 'ToolsPlatforms.Chart Analysis',
      text: 'ToolsPlatforms.Navigate charts with a smooth pinch to zoom feature',
      link: 'https://youtube.com/shorts/mQP_76Xk0pg',
    },
  ]
}

export const useTickmillTraderIOSVideosData = (): TickmillTraderVideoData[] => {
  const isMobile = useWindowResize()
  const { t } = useTranslation()

  return [
    {
      image: isMobile ? aboutMobile : aboutDesktop,
      title: t('ToolsPlatforms.About the app'),
      text: t('ToolsPlatforms.Keep control of your trading with easy access all in one place'),
      link: 'https://youtube.com/shorts/3LVr66iUvF4',
    },
    {
      image: isMobile ? managementMobile : managementDesktop,
      title: t('ToolsPlatforms.Account Management'),
      text: t('ToolsPlatforms.Get easy and secure login with face recognition and fingerprint'),
      link: 'https://youtube.com/shorts/2lJGeOkU6MQ',
    },
    {
      image: isMobile ? buysellMobile : buysellDesktop,
      title: t('ToolsPlatforms.Buy and Sell'),
      text: t('ToolsPlatforms.Seamless trading with low spreads at your fingertips'),
      link: 'https://youtube.com/shorts/_Z1iZyVWqt8',
    },
    {
      image: isMobile ? watchlistMobile : watchlistDesktop,
      title: t('ToolsPlatforms.Watchlist'),
      text: t('ToolsPlatforms.Create a watchlist with your favourite assets'),
      link: 'https://youtube.com/shorts/H7BxqgUNSpU',
    },
    {
      image: isMobile ? chartMobile : chartDesktop,
      title: t('ToolsPlatforms.Chart Analysis'),
      text: t('ToolsPlatforms.Navigate charts with a smooth pinch to zoom feature'),
      link: 'https://youtube.com/shorts/piKlJc7fE50',
    },
  ]
}

interface UseComparePlatformsData {
  comparePlatformsData: {
    name: string
    webTrader: string | boolean
    mobileApp: string | boolean
  }[]
}

export const useComparePlatformsData = (): UseComparePlatformsData => {
  const { t } = useTranslation()

  const comparePlatformsData = [
    { name: t('ToolsPlatforms.Technical Indicators'), webTrader: '80+', mobileApp: '60+' },
    { name: t('ToolsPlatforms.Advanced Charting Package'), webTrader: true, mobileApp: false },
    {
      name: t('ToolsPlatforms.Trading Directly from the Charts'),
      webTrader: true,
      mobileApp: false,
    },
    { name: t('ToolsPlatforms.Market Depth'), webTrader: true, mobileApp: false },
    { name: t('ToolsPlatforms.Performance Analysis'), webTrader: true, mobileApp: false },
    { name: t('ToolsPlatforms.Trade Journaling'), webTrader: true, mobileApp: false },
    { name: t('ToolsPlatforms.Pending Orders'), webTrader: true, mobileApp: true },
    { name: t('ToolsPlatforms.Custom Watchlists'), webTrader: true, mobileApp: true },
  ]

  return { comparePlatformsData }
}

interface UsePlatformDownloadLinksData {
  webTraderLink: string | undefined
  platformDownloadLinksData: {
    icon: React.ReactNode
    link: string | undefined
  }[]
}

export const usePlatformDownloadLinksData = (
  platformInfo: PlatformInfoDto[]
): UsePlatformDownloadLinksData => {
  const isMobile = useWindowResize()

  const platformDownloadLinksData = [
    {
      os: OperatingSystemEnum.IOS,
      icon: <AppStoreBanner width={119} height={40} />,
      condition: detectIOSOperatingSystem,
    },
    {
      os: OperatingSystemEnum.ANDROID,
      icon: <GooglePlayBanner width={135} height={40} />,
      condition: detectAndroidOperatingSystem,
    },
    {
      os: OperatingSystemEnum.ANDROID_APK,
      icon: <APKBanner />,
      condition: detectAndroidOperatingSystem,
    },
  ]

  const platformDownloadLinksDesktopExtras = [
    {
      icon: (
        <QRCode
          value={`${window.location.origin}/dl/tmt.html`}
          quietZone={32}
          size={640}
          qrStyle='squares'
          ecLevel='M'
          style={{ width: '60px', height: '60px', display: 'block' }}
        />
      ),
      link: undefined,
    },
  ]

  const getWebTraderLink = () => {
    return getDownloadLinks().find((x) => isWebTraderOperatingSystem(x.operatingSystem.id))
      ?.productLink
  }

  const getDownloadLink = (os: OperatingSystemEnum): string | undefined => {
    return getDownloadLinks()?.find((link) => link.operatingSystem.id === os)?.productLink
  }

  const getDownloadLinks = (): PlatformInfoPlatformLinkDto[] => {
    return platformInfo.find((p) => isTMTPlatformType(p.platfromType.id))?.platformLinks || []
  }

  const platformDownloadLinksMobileData = platformDownloadLinksData
    .filter((x) => x.condition?.())
    .map((x) => ({
      icon: x.icon,
      link: getDownloadLink(x.os),
    }))

  const platformDownloadLinksDesktopData = [
    ...platformDownloadLinksDesktopExtras,
    ...platformDownloadLinksData.map((x) => ({
      icon: x.icon,
      link: getDownloadLink(x.os),
    })),
  ]

  return {
    webTraderLink: getWebTraderLink(),
    platformDownloadLinksData: isMobile
      ? platformDownloadLinksMobileData
      : platformDownloadLinksDesktopData,
  }
}
